import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Users, Plus, Shield } from "lucide-react";
import { toast } from "sonner";

export default function Teams() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const r = await api.get("/teams");
      setTeams(r.data.teams || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <p className="label-eyebrow mb-2">{t("team.info")}</p>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight">{t("team.my_teams")}</h1>
          </div>
          {user && (
            <Link to="/teams/create">
              <button className="btn-volt px-6 py-3 flex items-center gap-2 text-xs tracking-widest uppercase font-bold">
                <Plus className="h-4 w-4" /> {t("team.create_team")}
              </button>
            </Link>
          )}
        </div>

        {/* Teams list */}
        {loading ? (
          <div className="text-center py-20">
            <div className="h-8 w-8 border-2 border-volt border-t-transparent animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
          </div>
        ) : teams.length === 0 ? (
          <div className="text-center py-20">
            <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground text-lg font-semibold">{t("team.no_teams")}</p>
            <p className="text-muted-foreground/60 text-sm mt-1">{t("team.no_teams_hint")}</p>
            {user && (
              <Link to="/teams/create" className="inline-block mt-6">
                <button className="btn-volt px-6 py-3 flex items-center gap-2 text-xs tracking-widest uppercase font-bold">
                  <Plus className="h-4 w-4" /> {t("team.create_team")}
                </button>
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.map((team) => (
              <Link
                key={team.id}
                to={`/teams/${team.id}`}
                className="bg-card border border-border hover:border-volt/40 transition-all group"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    {team.logo ? (
                      <div className="h-10 w-10 rounded-full overflow-hidden border border-border flex-shrink-0">
                        <img src={team.logo} alt={team.name} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <Shield className="h-8 w-8 text-volt" />
                    )}
                  </div>
                  <h3 className="font-heading text-xl tracking-tight mb-2 group-hover:text-volt transition-colors">
                    {team.name}
                  </h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Shield className="h-3 w-3" /> {team.sport}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" /> {team.roster?.length ?? 0}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
